/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Event Types Contract (Phase 4 §8)
 *
 * Manages `EventType` templates + their `EventTypeFieldDefinition` children.
 * See `docs/plans/design-21-eventtype-dynamic-fields.md` for the design.
 *
 * NOTE: The Prisma record type is exported here as `EventTypeRecord` so it
 * stays distinct from slug-like event type strings used at booking edges.
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";
import {
	EventTypeFieldDefinitionInputSchema,
	EventTypeFieldDefinitionSchema,
} from "./schemas/field-definitions";

// =============================================================================
// Shared sub-schemas
// =============================================================================

/**
 * Slug shape — stable, uppercase snake_case routing key, unique within an org.
 */
export const EventTypeSlugSchema = z
	.string()
	.min(1)
	.max(64)
	.regex(
		/^[A-Z][A-Z0-9_]*$/,
		"Slug must be UPPER_SNAKE_CASE and start with a letter"
	);

export const EventSchedulingTypeSchema = z.enum([
	"SOLO",
	"ROUND_ROBIN",
	"COLLECTIVE",
	"MANAGED",
]);

export const PeriodTypeSchema = z.enum([
	"UNLIMITED",
	"ROLLING",
	"RANGE",
	"FIXED_RANGE",
]);

/**
 * `scheduled` = user-bookable event template authored in the editor.
 * `conversation` = system-managed singleton per org used to colour AI-driven
 * conversation events on the calendar. The conversation row cannot be deleted
 * or have its slug / capacity / duration edited.
 */
export const EventTypeKindSchema = z.enum(["scheduled", "conversation"]);
export type EventTypeKind = z.infer<typeof EventTypeKindSchema>;

/**
 * Per-EventType duration mode.
 * - `auto`   — `durationMinutes` is computed at save time as
 *              `floor(slotDurationMinutes / max(maxPerSlotAgent, maxPerSlotSecretary))`,
 *              clamped to `[MIN_EVENT_DURATION_MINUTES, MAX_EVENT_DURATION_MINUTES]`.
 *              When neither role cap is set the default duration is used.
 *              The computation is stale-until-next-save when the org's slot
 *              duration changes (mirrors the deleted EventDurationsSection).
 * - `manual` — `durationMinutes` is whatever the user typed.
 */
export const EventTypeDurationStrategySchema = z.enum(["auto", "manual"]);
export type EventTypeDurationStrategy = z.infer<
	typeof EventTypeDurationStrategySchema
>;

/**
 * Range for `maxFutureEventsPerType` (mirrors the deleted slider's
 * `MAX_FUTURE_EVENTS_SLIDER`).
 */
export const MAX_FUTURE_EVENTS_PER_TYPE_MIN = 1;
export const MAX_FUTURE_EVENTS_PER_TYPE_MAX = 20;

// =============================================================================
// EventType record
// =============================================================================

/**
 * Output shape for a single EventType row.
 */
export const EventTypeRecordSchema = z.object({
	id: z.string(),
	orgId: z.string(),
	slug: EventTypeSlugSchema,
	label: z.string(),
	isActive: z.boolean(),
	position: z.number().int(),
	kind: EventTypeKindSchema,
	// Three independent color channels — see prisma schema `EventType.colorBackground/Ring/Text`.
	// All nullable. When any channel is null the UI falls back to the ring
	// value (when set) or the org theme default.
	colorBackground: z.string().nullable(),
	colorRing: z.string().nullable(),
	colorText: z.string().nullable(),
	durationStrategy: EventTypeDurationStrategySchema,
	durationMinutes: z
		.number()
		.int()
		.min(1)
		.max(60 * 24),
	// Per-role slot ceiling. `null` = unlimited for that role. Both columns
	// replace the legacy single `maxPerSlot` field.
	maxPerSlotAgent: z.number().int().min(1).nullable(),
	maxPerSlotSecretary: z.number().int().min(1).nullable(),
	allowMultipleFutureEvents: z.boolean(),
	allowMultipleEventsPerDay: z.boolean(),
	maxFutureEventsPerType: z
		.number()
		.int()
		.min(MAX_FUTURE_EVENTS_PER_TYPE_MIN)
		.max(MAX_FUTURE_EVENTS_PER_TYPE_MAX)
		.nullable(),
	// Per-role booking horizon: max days ahead of "today" (org timezone) this
	// type may be booked. `null` = unlimited for that role.
	bookingHorizonAgentDays: z.number().int().min(1).nullable(),
	bookingHorizonSecretaryDays: z.number().int().min(1).nullable(),
	minimumBookingNotice: z.number().int().min(0),
	beforeBufferMinutes: z.number().int().min(0),
	afterBufferMinutes: z.number().int().min(0),
	schedulingType: EventSchedulingTypeSchema,
	seatsPerTimeSlot: z.number().int().min(1).nullable(),
	periodType: PeriodTypeSchema,
	periodStartDate: z.string().datetime().nullable(),
	periodEndDate: z.string().datetime().nullable(),
	periodDays: z.number().int().min(0).nullable(),
	bookingLimits: z.record(z.string(), z.unknown()).nullable(),
	recurringRule: z.record(z.string(), z.unknown()).nullable(),
	metadata: z.record(z.string(), z.unknown()).nullable(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	deletedAt: z.string().datetime().nullable(),
	/**
	 * Field definitions (populated when `include=fields` is passed to `list`
	 * or `get`). Empty array when the EventType has no custom fields.
	 */
	fieldDefinitions: z.array(EventTypeFieldDefinitionSchema).optional(),
});

export type EventTypeRecord = z.infer<typeof EventTypeRecordSchema>;

/**
 * Input shape for `upsert` — a subset of the record with server-managed
 * fields excluded. Pass `id` to update, omit `id` to create.
 */
export const EventTypeUpsertInputSchema = z
	.object({
		id: z.string().optional(),
		/**
		 * Required on create. On create, if `slug` is omitted the server
		 * derives one from `label` via `labelToSlug()`.
		 */
		slug: EventTypeSlugSchema.optional(),
		label: z.string().min(1).max(200),
		isActive: z.boolean().optional(),
		position: z.number().int().min(0).optional(),
		colorBackground: z
			.string()
			.regex(/^#[0-9a-fA-F]{6}$/)
			.nullable()
			.optional(),
		colorRing: z
			.string()
			.regex(/^#[0-9a-fA-F]{6}$/)
			.nullable()
			.optional(),
		colorText: z
			.string()
			.regex(/^#[0-9a-fA-F]{6}$/)
			.nullable()
			.optional(),
		// Server rejects user-supplied `kind: 'conversation'` upserts — see
		// event-type.service.ts. The conversation row is seeded by the
		// migration / OrganizationCreated saga and surfaces here only as an
		// output field on the record schema.
		kind: EventTypeKindSchema.optional(),
		durationStrategy: EventTypeDurationStrategySchema.optional(),
		durationMinutes: z
			.number()
			.int()
			.min(1)
			.max(60 * 24),
		maxPerSlotAgent: z.number().int().min(1).nullable().optional(),
		maxPerSlotSecretary: z.number().int().min(1).nullable().optional(),
		allowMultipleFutureEvents: z.boolean().optional(),
		allowMultipleEventsPerDay: z.boolean().optional(),
		maxFutureEventsPerType: z
			.number()
			.int()
			.min(MAX_FUTURE_EVENTS_PER_TYPE_MIN)
			.max(MAX_FUTURE_EVENTS_PER_TYPE_MAX)
			.nullable()
			.optional(),
		bookingHorizonAgentDays: z.number().int().min(1).nullable().optional(),
		bookingHorizonSecretaryDays: z.number().int().min(1).nullable().optional(),
		minimumBookingNotice: z.number().int().min(0).optional(),
		beforeBufferMinutes: z.number().int().min(0).optional(),
		afterBufferMinutes: z.number().int().min(0).optional(),
		schedulingType: EventSchedulingTypeSchema.optional(),
		seatsPerTimeSlot: z.number().int().min(1).nullable().optional(),
		periodType: PeriodTypeSchema.optional(),
		periodStartDate: z.string().datetime().nullable().optional(),
		periodEndDate: z.string().datetime().nullable().optional(),
		periodDays: z.number().int().min(0).nullable().optional(),
		bookingLimits: z.record(z.string(), z.unknown()).nullable().optional(),
		recurringRule: z.record(z.string(), z.unknown()).nullable().optional(),
		metadata: z.record(z.string(), z.unknown()).nullable().optional(),
	})
	.strict();

export type EventTypeUpsertInput = z.infer<typeof EventTypeUpsertInputSchema>;

// =============================================================================
// Auto-duration helper
// =============================================================================

/**
 * Default duration value used as a fallback when auto math has no inputs.
 * Mirrors `DEFAULT_EVENT_DURATION_MINUTES` from `config/constants.ts` —
 * duplicated here to keep the contract self-contained (no upward import).
 */
const DEFAULT_AUTO_DURATION_MINUTES = 30;

/**
 * Compute the duration for an EventType in `auto` strategy.
 *
 * `floor(slotDurationMinutes / max(maxPerSlotAgent, maxPerSlotSecretary))`,
 * clamped to `[minMinutes, maxMinutes]` (defaults [5, 480], matching
 * `MIN_EVENT_DURATION_MINUTES` / `MAX_EVENT_DURATION_MINUTES`).
 *
 * When neither role cap is set, OR the floor result is `<= 0`, the function
 * returns `DEFAULT_AUTO_DURATION_MINUTES` clamped to the same bounds. This is
 * the same fallback chain the deleted `EventDurationsSection` used.
 */
export function computeAutoDurationMinutes(params: {
	slotDurationMinutes: number;
	maxPerSlotAgent: number | null;
	maxPerSlotSecretary: number | null;
	minMinutes?: number;
	maxMinutes?: number;
}): number {
	const minMinutes = params.minMinutes ?? 5;
	const maxMinutes = params.maxMinutes ?? 480;
	const clamp = (n: number): number =>
		Math.max(minMinutes, Math.min(maxMinutes, n));

	const slot =
		Number.isFinite(params.slotDurationMinutes) &&
		params.slotDurationMinutes > 0
			? params.slotDurationMinutes
			: 0;
	const agent =
		typeof params.maxPerSlotAgent === "number" && params.maxPerSlotAgent > 0
			? params.maxPerSlotAgent
			: 0;
	const secretary =
		typeof params.maxPerSlotSecretary === "number" &&
		params.maxPerSlotSecretary > 0
			? params.maxPerSlotSecretary
			: 0;
	const cap = Math.max(agent, secretary);

	if (slot <= 0 || cap <= 0) {
		return clamp(DEFAULT_AUTO_DURATION_MINUTES);
	}
	const computed = Math.floor(slot / cap);
	if (computed <= 0) {
		return clamp(DEFAULT_AUTO_DURATION_MINUTES);
	}
	return clamp(computed);
}

// =============================================================================
// Contract
// =============================================================================

export const eventTypesContract = {
	list: oc
		.route({
			method: "GET",
			path: "/api/event-types",
			summary: "List EventTypes for the current org",
			tags: ["EventTypes"],
		})
		.input(
			z
				.object({
					includeInactive: z
						.string()
						.regex(/^(true|false)$/)
						.optional(),
					includeFields: z
						.string()
						.regex(/^(true|false)$/)
						.optional(),
				})
				.optional()
		)
		.output(
			z.object({
				eventTypes: z.array(EventTypeRecordSchema),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: { message: "Failed to list event types" },
		}),

	get: oc
		.route({
			method: "GET",
			path: "/api/event-types/{id}",
			summary: "Get a single EventType with its field definitions",
			tags: ["EventTypes"],
		})
		.input(
			z.object({
				id: z.string().min(1),
				includeFields: z
					.string()
					.regex(/^(true|false)$/)
					.optional(),
			})
		)
		.output(EventTypeRecordSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "EventType not found",
				data: z.object({ id: z.string() }),
			},
			DATABASE_ERROR: { message: "Failed to retrieve event type" },
		}),

	upsert: oc
		.route({
			method: "POST",
			path: "/api/event-types",
			summary: "Create or update an EventType",
			tags: ["EventTypes"],
		})
		.input(EventTypeUpsertInputSchema)
		.output(EventTypeRecordSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			VALIDATION_ERROR: {
				message: "Invalid EventType payload",
				data: z.object({
					errors: z.array(z.object({ path: z.string(), message: z.string() })),
				}),
			},
			CONFLICT: {
				message: "Slug already in use for this org",
				data: z.object({ slug: z.string() }),
			},
			DATABASE_ERROR: { message: "Failed to save event type" },
		}),

	deactivate: oc
		.route({
			method: "POST",
			path: "/api/event-types/{id}/deactivate",
			summary: "Soft-delete an EventType (sets isActive=false)",
			tags: ["EventTypes"],
		})
		.input(z.object({ id: z.string().min(1) }))
		.output(
			z.object({
				deactivated: z.boolean(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "EventType not found",
				data: z.object({ id: z.string() }),
			},
			CONFLICT: {
				message: "EventType still has active bookings",
				data: z.object({ id: z.string(), bookingCount: z.number() }),
			},
			VALIDATION_ERROR: {
				message:
					"EventType cannot be deactivated (e.g. system-managed conversation row)",
				data: z.object({
					errors: z.array(z.object({ path: z.string(), message: z.string() })),
				}),
			},
			DATABASE_ERROR: { message: "Failed to deactivate event type" },
		}),

	reorder: oc
		.route({
			method: "POST",
			path: "/api/event-types/reorder",
			summary: "Persist a drag-reordered list of EventTypes",
			tags: ["EventTypes"],
		})
		.input(
			z.object({
				items: z
					.array(
						z.object({
							id: z.string().min(1),
							position: z.number().int().min(0),
						})
					)
					.max(100),
			})
		)
		.output(z.object({ reordered: z.number().int().min(0) }))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: { message: "Failed to reorder event types" },
		}),

	fields: {
		list: oc
			.route({
				method: "GET",
				path: "/api/event-types/{id}/fields",
				summary: "List field definitions for an EventType",
				tags: ["EventTypes", "Fields"],
			})
			.input(z.object({ id: z.string().min(1) }))
			.output(
				z.object({
					fields: z.array(EventTypeFieldDefinitionSchema),
				})
			)
			.errors({
				UNAUTHORIZED: commonErrors.UNAUTHORIZED,
				NOT_FOUND: {
					message: "EventType not found",
					data: z.object({ id: z.string() }),
				},
				DATABASE_ERROR: { message: "Failed to list fields" },
			}),

		add: oc
			.route({
				method: "POST",
				path: "/api/event-types/{id}/fields",
				summary: "Add a field definition to an EventType",
				tags: ["EventTypes", "Fields"],
			})
			.input(
				EventTypeFieldDefinitionInputSchema.extend({
					id: z.string().min(1),
				})
			)
			.output(EventTypeFieldDefinitionSchema)
			.errors({
				UNAUTHORIZED: commonErrors.UNAUTHORIZED,
				NOT_FOUND: {
					message: "EventType not found",
					data: z.object({ id: z.string() }),
				},
				CONFLICT: {
					message: "Field name already in use on this EventType",
					data: z.object({ name: z.string() }),
				},
				VALIDATION_ERROR: {
					message: "Invalid field definition",
					data: z.object({
						errors: z.array(
							z.object({ path: z.string(), message: z.string() })
						),
					}),
				},
				DATABASE_ERROR: { message: "Failed to add field" },
			}),

		update: oc
			.route({
				method: "PATCH",
				path: "/api/event-types/{id}/fields/{fieldId}",
				summary: "Update mutable attributes of a field definition",
				tags: ["EventTypes", "Fields"],
			})
			.input(
				z.object({
					id: z.string().min(1),
					fieldId: z.string().min(1),
					// `name` is immutable post-create (see §12 risk 2); not accepted.
					label: z.string().min(1).max(200).optional(),
					required: z.boolean().optional(),
					position: z.number().int().min(0).optional(),
					options: z
						.array(
							z.object({
								value: z.string().min(1).max(100),
								label: z.string().min(1).max(200),
								i18nKey: z.string().max(200).optional(),
							})
						)
						.optional(),
					validation: z
						.object({
							min: z.number().optional(),
							max: z.number().optional(),
							pattern: z.string().max(500).optional(),
							step: z.number().optional(),
						})
						.optional(),
					gridVisible: z.boolean().optional(),
					gridWidth: z.number().int().min(40).max(1000).nullable().optional(),
				})
			)
			.output(EventTypeFieldDefinitionSchema)
			.errors({
				UNAUTHORIZED: commonErrors.UNAUTHORIZED,
				NOT_FOUND: {
					message: "Field not found",
					data: z.object({ fieldId: z.string() }),
				},
				DATABASE_ERROR: { message: "Failed to update field" },
			}),

		remove: oc
			.route({
				method: "DELETE",
				path: "/api/event-types/{id}/fields/{fieldId}",
				summary: "Soft-delete a field definition (preserves JSON values)",
				tags: ["EventTypes", "Fields"],
			})
			.input(
				z.object({
					id: z.string().min(1),
					fieldId: z.string().min(1),
				})
			)
			.output(z.object({ removed: z.boolean() }))
			.errors({
				UNAUTHORIZED: commonErrors.UNAUTHORIZED,
				NOT_FOUND: {
					message: "Field not found",
					data: z.object({ fieldId: z.string() }),
				},
				DATABASE_ERROR: { message: "Failed to remove field" },
			}),

		reorder: oc
			.route({
				method: "POST",
				path: "/api/event-types/{id}/fields/reorder",
				summary: "Persist drag-reorder of field positions",
				tags: ["EventTypes", "Fields"],
			})
			.input(
				z.object({
					id: z.string().min(1),
					items: z
						.array(
							z.object({
								fieldId: z.string().min(1),
								position: z.number().int().min(0),
							})
						)
						.max(100),
				})
			)
			.output(z.object({ reordered: z.number().int().min(0) }))
			.errors({
				UNAUTHORIZED: commonErrors.UNAUTHORIZED,
				NOT_FOUND: {
					message: "EventType not found",
					data: z.object({ id: z.string() }),
				},
				DATABASE_ERROR: { message: "Failed to reorder fields" },
			}),
	},
};

// =============================================================================
// Types
// =============================================================================

export type EventTypesContract = typeof eventTypesContract;
